/**
 * Native app wiring: everything the Capacitor shell needs at boot, in one call from main.ts (before mount, so the
 * app-URL listener is the first to attach and receives the cold-start launch URL).
 *
 * - App URLs: one dispatcher (src/native/appUrl.ts) for quick actions (nativeShortcuts.ts), OAuth callbacks
 *   and deep links to `/dashboard/...` (universal / app links, or `com.joinlighthouse.app://dashboard/...`).
 * - Lifecycle: on resume the agent streams reconnect at once, the queue refreshes and the push token is
 *   re-registered (hourly at most). Voice handles its own pause/resume (src/voice/platform.ts).
 * - Network: the agent streams hold their backoff while offline and reconnect when it returns.
 * - Push: tap routing + foreground banner (nativePushActions.ts), silent re-registration on an authenticated
 *   launch, the app icon badge = the queue's needs-you count, delivered notifications for a conversation are
 *   cleared when it opens.
 * - External links open in the in-app browser (src/native/browser.ts).
 * - Every backend request says which app sent it (src/native/platformHeaders.ts).
 *
 * The web gets none of this (every call below is a no-op there).
 */
import axios from 'axios'
import { watch } from 'vue'
import type { Router } from 'vue-router'
import useAgentQueueCounts, { refreshAgentQueue } from '@/agent/useAgentQueueCounts'
import { reconnectAgentStreams, setAgentStreamsOnline } from '@/agent/stream'
import { syncNativePushRegistration } from '@/composables/usePushNotifications'
import useUser from '@/composables/useUser'
import { useAuthStore } from '@/stores/auth'
import { clearConversationNotifications, initNativePushActionRouting } from '@/utils/nativePushActions'
import { initNativeShortcuts } from '@/utils/nativeShortcuts'
import { isNativePlatform } from '@/utils/platform'
import { initAppUrlDispatcher, onAppUrlOpen } from './appUrl'
import { clearAppBadge, recheckBadgePermission, setAppBadge } from './badge'
import { installNativeLinkShim } from './browser'
import { initAppLifecycle, onAppResume } from './lifecycle'
import { isOnline, onNetworkChange, startNetworkWatch } from './network'
import { installNativePlatformHeaders } from './platformHeaders'

const APP_HOSTS = /(^|\.)joinlighthouse\.ai$/i

/** `/dashboard/...` from an app URL (https on our host, or the custom scheme), else null. */
export function dashboardPathFromAppUrl(raw: string): string | null {
    let url: URL
    try {
        url = new URL(raw)
    } catch {
        return null
    }
    let path: string
    if (url.protocol === 'https:' && APP_HOSTS.test(url.hostname)) {
        path = url.pathname
    } else if (url.protocol === 'com.joinlighthouse.app:') {
        // com.joinlighthouse.app://dashboard/inbox → host "dashboard", path "/inbox"
        path = `/${url.host}${url.pathname}`.replace(/\/+$/, '')
    } else {
        return null
    }
    if (!/^\/dashboard(\/|$)/.test(path)) return null
    return `${path}${url.search}${url.hash}`
}

let started = false

export function initNativeApp(router: Router): void {
    if (started || !isNativePlatform()) return
    started = true

    // First, so every backend call (fetch and axios) carries X-Lighthouse-Platform / -App-Version.
    installNativePlatformHeaders(axios)
    void initAppUrlDispatcher()
    void initAppLifecycle()
    installNativeLinkShim()
    startNetworkWatch()

    // Generic deep links. Subscribed first, so the newer, specific handlers (quick actions, OAuth) win.
    onAppUrlOpen((url) => {
        const path = dashboardPathFromAppUrl(url)
        if (!path) return false
        void router.isReady().then(() => router.push(path))
        return true
    })
    initNativeShortcuts(router)
    void initNativePushActionRouting(router)

    const auth = useAuthStore()
    const { user } = useUser()
    const { counts, loaded } = useAgentQueueCounts()
    const signedIn = () => auth.isAuthenticated && !!user.value?.id

    // ── streams, queue and push token across background / foreground ──
    setAgentStreamsOnline(isOnline())
    onNetworkChange((online) => {
        setAgentStreamsOnline(online)
        if (!online) return
        reconnectAgentStreams()
        if (signedIn()) void refreshAgentQueue()
    })
    onAppResume(() => {
        reconnectAgentStreams()
        if (!signedIn()) return
        void refreshAgentQueue()
        void syncNativePushRegistration()
        // Notification permission may have changed in Settings while away.
        recheckBadgePermission()
        if (loaded.value) void setAppBadge(counts.value.needs_you)
    })

    // An authenticated launch (or a new sign-in): re-register the token if push is already allowed.
    watch(
        () => (signedIn() ? user.value?.id : null),
        (id) => {
            if (!id) {
                void clearAppBadge()
                return
            }
            recheckBadgePermission()
            void syncNativePushRegistration({ force: true })
        },
        { immediate: true },
    )

    // The app icon badge follows the queue's "needs you" count.
    watch(
        () => (loaded.value && signedIn() ? counts.value.needs_you : null),
        (n) => {
            if (n !== null) void setAppBadge(n)
        },
        { immediate: true },
    )

    // Opening a conversation clears its delivered notifications.
    router.afterEach((to) => {
        const id = to.params?.conversationId
        if (typeof id === 'string' && id) void clearConversationNotifications(id)
    })
}
